import React, { FC, useState, useEffect } from "react";
import { FormGroup, Label, FormFeedback } from "reactstrap";

interface DatePickerProps {
    label?: string;
    name: string;
    value: string | null;
    setValue: (name: string, value: string | null) => void;
    setError?: (name: string, error: boolean) => void;
    error?: boolean;
    errorMessage?: string;
    required?: boolean;
    disabled?: boolean;
    design?: "flex" | "default";
    passCol?: string;
    tabIndex?: number;
}

const DatePicker: FC<DatePickerProps> = ({
    label,
    name,
    value,
    setValue,
    setError,
    error,
    errorMessage,
    required = false,
    disabled = false,
    design = "default",
    passCol,
    tabIndex,
}) => {
    const formatToDisplay = (isoDate: string) => {
        const date = new Date(isoDate);
        const dd = date.getUTCDate().toString().padStart(2, "0");
        const mm = (date.getUTCMonth() + 1).toString().padStart(2, "0");
        const yyyy = date.getUTCFullYear();
        return `${dd}/${mm}/${yyyy}`;
    };

    const formatToISO = (dd: number, mm: number, yyyy: number): string => {
        return new Date(Date.UTC(yyyy, mm - 1, dd)).toISOString();
    };

    const validateAndCorrectDate = (input: string): string => {
        const parts = input.split("/");

        if (parts.length < 3) return input;

        let [dd, mm, yyyy] = parts.map(Number);

        if (dd > 31) dd = 1;
        else if (dd < 1) dd = 1;

        if (mm > 12) mm = 1;
        else if (mm < 1) mm = 1;

        return `${dd.toString().padStart(2, "0")}/${mm.toString().padStart(2, "0")}/${yyyy || ""}`;
    };

    const [inputValue, setInputValue] = useState(value ? formatToDisplay(value) : "");
    const [windowWidth, setWindowWidth] = useState<number>(window.innerWidth);

    useEffect(() => {
        const handleResize = () => setWindowWidth(window.innerWidth);
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    useEffect(() => {
        if (value) {
            setInputValue(formatToDisplay(value));
        } else {
            setInputValue("");
        }
    }, [value]);

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        let input = e.target.value.replace(/[^0-9/]/g, "");

        if (input.length > 2 && input[2] !== "/") input = input.slice(0, 2) + "/" + input.slice(2);
        if (input.length > 5 && input[5] !== "/") input = input.slice(0, 5) + "/" + input.slice(5);

        if (input.split("/").length === 3) {
            const correctedInput = validateAndCorrectDate(input);
            setInputValue(correctedInput);

            const [dd, mm, yyyy] = correctedInput.split("/").map(Number);
            if (dd && mm && yyyy && yyyy.toString().length === 4) {
                setValue(name, formatToISO(dd, mm, yyyy));
                setError?.(name, false);
            } else {
                setValue(name, "");
                setError?.(name, true);
            }
        } else {
            setInputValue(input);
            setValue(name, "");
            setError?.(name, true);
        }
    };

    const handleBlur = () => {
        const correctedValue = validateAndCorrectDate(inputValue);
        setInputValue(correctedValue);

        const [dd, mm, yyyy] = correctedValue.split("/").map(Number);
        if (dd && mm && yyyy && yyyy.toString().length === 4) {
            setValue(name, formatToISO(dd, mm, yyyy));
        } else {
            setValue(name, "");
        }
    };

    const flexClass = design === "flex" && windowWidth > 768 ? "d-flex align-items-center" : "";
    const labelWidthClass = design === "flex" && windowWidth > 768 ? "col-3" : "";
    const passColClass = windowWidth > 768 ? passCol : "";

    return (
        <FormGroup className={flexClass}>
            {label && (
                <Label for={name} className={`${design === "flex" ? "mr-5 " : ""}${labelWidthClass}`}>
                    {label}
                    {required && <span className="text-danger ml-2">*</span>}
                </Label>
            )}

            <div className={passColClass}>
                {error && <FormFeedback className="d-block">{errorMessage}</FormFeedback>}
                <input
                    type="text"
                    id={name}
                    name={name}
                    tabIndex={tabIndex}
                    value={inputValue}
                    onChange={handleInputChange}
                    onBlur={handleBlur}
                    placeholder="DD/MM/YYYY"
                    maxLength={10}
                    className={`form-control ${error ? "is-invalid" : ""}`}
                    disabled={disabled}
                />
            </div>
        </FormGroup>
    );
};

export default DatePicker;
